// ─────────────────────────────────────────────────────────────────────────
// ModuleFieldConfigEditor — the full List View / Filter / Form field editor
// (badges, action buttons, lookups, secondary fields, render types, drag
// reorder). Configure once per role; every user with that role sees the
// same fields — this is the role-scoped counterpart of what used to be a
// per-user editor in UsersSection.jsx (moved here in full, not simplified,
// so no admin capability is lost).
// ─────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button, Input, Radio, Select, Space, Spin, Switch, Tabs, Tooltip, Typography, Upload, message,
} from 'antd';
import {
  CloseOutlined, DownOutlined, HolderOutlined, LinkOutlined,
  PlusOutlined, RightOutlined, TagsOutlined, UploadOutlined, UserOutlined,
} from '@ant-design/icons';
import TemplateEditor from '../../../../components/TemplateEditor';
import {
  getArraySubfields,
  getAvailableCollections,
  getAvailableModules,
  getCollectionFields,
  getRoleModuleFields,
  getRoleModuleFieldsWithMeta,
  updateRoleModuleFieldConfig,
  uploadListViewActionIcon,
} from '../../../../services/adminApi';
import { getDropdownValues } from '../../../../services/dropdownApi';
import '../../../../styles/UserSettingsSection.css';

const { Text } = Typography;
const { Option } = Select;

const DEFAULT_MODULE_TABS = [
  { key: 'job', label: 'Jobs' },
  { key: 'candidate', label: 'Candidates' },
  { key: 'submissions', label: 'Submissions' },
  { key: 'billing', label: 'Billing' },
  { key: 'payroll', label: 'Payroll' },
];

const RENDER_TYPES = [
  { key: '', label: 'Text', icon: <span style={{ fontSize: 14, fontWeight: 700 }}>Aa</span> },
  { key: 'badge', label: 'Badge', icon: <TagsOutlined style={{ fontSize: 16 }} /> },
  { key: 'avatar', label: 'Avatar', icon: <UserOutlined style={{ fontSize: 16 }} /> },
  { key: 'link', label: 'Link', icon: <LinkOutlined style={{ fontSize: 16 }} /> },
];

const BADGE_COLORS = [
  { color: '#52c41a', bg: '#f6ffed' }, { color: '#1677ff', bg: '#e6f7ff' },
  { color: '#fa8c16', bg: '#fff7e6' }, { color: '#ff4d4f', bg: '#fff2f0' },
  { color: '#722ed1', bg: '#f9f0ff' }, { color: '#13c2c2', bg: '#e6fffb' },
  { color: '#eb2f96', bg: '#fff0f6' }, { color: '#faad14', bg: '#fffbe6' },
];

const API_MODULE_MAP = { job: 'jobs', candidate: 'candidates', submissions: 'submissions', billing: 'billing', payroll: 'payroll' };

function moduleTitle(value) {
  return String(value || '')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

function normalizeModuleTab(module) {
  if (typeof module === 'string') {
    return { key: module, apiModule: module, label: moduleTitle(module) };
  }

  const key = (
    module?.menuKey ?? module?.key ?? module?.moduleKey ?? module?.value ?? module?.module ??
    module?.apiModule ?? module?.name ?? module?.modulename ?? module?.menuName
  );
  if (!key) return null;

  const apiModule = (
    module?.apiModule ?? module?.api_module ?? module?.module ?? module?.value ??
    module?.menuKey ?? module?.key ?? module?.name ?? module?.modulename ?? module?.menuName ?? key
  );

  return {
    key,
    apiModule,
    label: (
      module?.label ?? module?.moduleLabel ?? module?.moduleName ??
      module?.name ?? module?.modulename ?? module?.menuName ?? moduleTitle(key)
    ),
  };
}

function normalizeModuleTabs(modules) {
  const source = Array.isArray(modules) && modules.length ? modules : DEFAULT_MODULE_TABS;
  const seen = new Set();
  const tabs = source
    .map(normalizeModuleTab)
    .filter(Boolean)
    .filter((module) => {
      if (seen.has(module.key)) return false;
      seen.add(module.key);
      return true;
    });

  return tabs.length ? tabs : DEFAULT_MODULE_TABS;
}

// ─────────────────────────────────────────────────────────────
// List-view field detail panel (badges, avatars, links, secondary fields,
// lookups, action buttons)
// ─────────────────────────────────────────────────────────────
function ListViewDetails({
  field, collections, collFields, subfields,
  onCollectionChange, onLookupFieldChange,
  addSecondaryField, removeSecondaryField,
  addActionButton, updateActionButton, removeActionButton,
  moduleKey, fieldKey, update,
}) {
  const [newSecondary, setNewSecondary] = useState('');
  const [iconUploading, setIconUploading] = useState({});
  const [detecting, setDetecting] = useState(false);

  function setBadgeMode(mode) {
    if (mode === 'common') update({ commonStyle: { color: '#1677ff', bg: '#e6f7ff', variant: 'badge' }, valueStyles: [] });
    else update({ commonStyle: null });
  }
  function updateCommonStyle(patch) {
    update({ commonStyle: { ...(field.commonStyle ?? { color: '#595959', bg: '#f5f5f5', variant: 'badge' }), ...patch } });
  }
  function addValueStyle() {
    update({ valueStyles: [...(field.valueStyles ?? []), { value: '', label: '', color: '#595959', bg: '#f5f5f5', variant: 'badge' }] });
  }
  function updateValueStyle(idx, patch) {
    const styles = [...(field.valueStyles ?? [])];
    styles[idx] = { ...styles[idx], ...patch };
    update({ valueStyles: styles });
  }
  function removeValueStyle(idx) {
    update({ valueStyles: (field.valueStyles ?? []).filter((_, i) => i !== idx) });
  }
  async function autoDetectValues() {
    setDetecting(true);
    try {
      const apiMod = field.apiModule ?? API_MODULE_MAP[moduleKey] ?? moduleKey;
      const result = await getDropdownValues(apiMod, fieldKey, '', 50, 0);
      const items = Array.isArray(result) ? result : (result?.data ?? []);
      const styles = items.map((item, i) => {
        const val = typeof item === 'object' ? (item.value ?? item.label ?? '') : String(item);
        const lbl = typeof item === 'object' ? (item.label ?? item.value ?? '') : String(item);
        return { value: String(val), label: String(lbl), variant: 'badge', ...BADGE_COLORS[i % BADGE_COLORS.length] };
      });
      if (styles.length === 0) { message.info('No values found for this field'); return; }
      update({ valueStyles: styles });
    } catch { message.error('Could not detect values'); }
    finally { setDetecting(false); }
  }

  async function handleIconUpload(btnIdx, file) {
    setIconUploading((p) => ({ ...p, [btnIdx]: true }));
    try {
      const result = await uploadListViewActionIcon(file);
      updateActionButton(btnIdx, {
        iconUrl: result?.iconUrl ?? result?.url ?? '',
        iconPath: result?.iconPath ?? result?.path ?? '',
      });
    } catch { message.error('Icon upload failed'); }
    finally { setIconUploading((p) => ({ ...p, [btnIdx]: false })); }
    return false;
  }

  const renderType = field.renderType ?? '';
  const isCommon = Boolean(field.commonStyle);
  const cs = field.commonStyle ?? {};
  const vs = field.valueStyles ?? [];
  const rc = field.renderConfig ?? {};

  return (
    <>
      <div className="uss-detail-section">
        <div className="uss-detail-label">Display Style</div>
        <div className="uss-detail-row">
          {RENDER_TYPES.map((rt) => (
            <button key={rt.key}
              className={`uss-render-type-btn${renderType === rt.key ? ' active' : ''}`}
              onClick={() => update({ renderType: rt.key, commonStyle: null, valueStyles: [], renderConfig: null })}>
              {rt.icon}<span>{rt.label}</span>
            </button>
          ))}
        </div>

        {/* ── Badge color config ── */}
        {renderType === 'badge' && (
          <div className="uss-badge-config">
            <Radio.Group size="small" value={isCommon ? 'common' : 'perValue'}
              onChange={(e) => setBadgeMode(e.target.value)} style={{ marginBottom: 8 }}>
              <Radio value="perValue">Per value</Radio>
              <Radio value="common">Same for all</Radio>
            </Radio.Group>

            {isCommon && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <Tooltip title="Text color">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input type="color" value={cs.color || '#595959'} style={{ width: 28, height: 24, border: 'none', cursor: 'pointer', padding: 0 }}
                      onChange={(e) => updateCommonStyle({ color: e.target.value })} />
                    <Text style={{ fontSize: 10 }}>Text</Text>
                  </div>
                </Tooltip>
                <Tooltip title="Background color">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input type="color" value={cs.bg || '#f5f5f5'} style={{ width: 28, height: 24, border: 'none', cursor: 'pointer', padding: 0 }}
                      onChange={(e) => updateCommonStyle({ bg: e.target.value })} />
                    <Text style={{ fontSize: 10 }}>BG</Text>
                  </div>
                </Tooltip>
                <Select size="small" style={{ width: 80 }} value={cs.variant || 'badge'}
                  options={[{ value: 'badge', label: 'Badge' }, { value: 'dot', label: 'Dot' }, { value: 'tag', label: 'Tag' }]}
                  onChange={(v) => updateCommonStyle({ variant: v })} />
                <span style={{ padding: '1px 12px', borderRadius: 20, fontSize: 11, fontWeight: 500, color: cs.color || '#595959', background: cs.bg || '#f5f5f5' }}>
                  Preview
                </span>
              </div>
            )}

            {!isCommon && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <Text style={{ fontSize: 11, color: '#555', fontWeight: 600 }}>Value → Style mapping</Text>
                  <Space size={6}>
                    <Button size="small" loading={detecting} onClick={autoDetectValues}>Auto-detect values</Button>
                    <Button size="small" icon={<PlusOutlined />} onClick={addValueStyle}>Add</Button>
                  </Space>
                </div>
                {vs.length === 0 && (
                  <Text type="secondary" style={{ fontSize: 12 }}>Click "Auto-detect values" to populate from live data, or "Add" to map manually.</Text>
                )}
                {vs.map((style, idx) => (
                  <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 62px 62px 54px 26px', gap: 5, alignItems: 'center', marginBottom: 6 }}>
                    <Input size="small" placeholder="raw value" value={style.value ?? ''}
                      onChange={(e) => updateValueStyle(idx, { value: e.target.value })} />
                    <Input size="small" placeholder="display label" value={style.label ?? ''}
                      onChange={(e) => updateValueStyle(idx, { label: e.target.value })} />
                    <Tooltip title="Text color">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <input type="color" value={style.color || '#595959'} style={{ width: 28, height: 24, border: 'none', cursor: 'pointer', padding: 0 }}
                          onChange={(e) => updateValueStyle(idx, { color: e.target.value })} />
                        <Text style={{ fontSize: 10 }}>Text</Text>
                      </div>
                    </Tooltip>
                    <Tooltip title="Background color">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <input type="color" value={style.bg || '#f5f5f5'} style={{ width: 28, height: 24, border: 'none', cursor: 'pointer', padding: 0 }}
                          onChange={(e) => updateValueStyle(idx, { bg: e.target.value })} />
                        <Text style={{ fontSize: 10 }}>BG</Text>
                      </div>
                    </Tooltip>
                    <span style={{ padding: '1px 6px', borderRadius: 20, fontSize: 11, fontWeight: 500, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: style.color || '#595959', background: style.bg || '#f5f5f5' }}>
                      {style.label || style.value || '…'}
                    </span>
                    <Button size="small" type="text" danger icon={<CloseOutlined />} onClick={() => removeValueStyle(idx)} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Array value config ── */}
        <div style={{ marginTop: 10 }}>
          <Text style={{ fontSize: 11, color: '#555', display: 'block', marginBottom: 3 }}>
            Array item field (latest item)
          </Text>
          <Select
            size="small"
            showSearch
            allowClear
            style={{ width: '100%' }}
            placeholder={subfields.length > 0 ? 'Select a child field from the array' : 'Enter child field, e.g. clientBudgetStart'}
            value={rc.lastItemField || undefined}
            options={subfields.map((sf) => ({ value: sf, label: sf }))}
            onChange={(v) => update({
              renderConfig: {
                ...rc,
                lastItemField: v ?? '',
              },
            })}
            onSearch={(value) => {
              if (!subfields.includes(value) && value) {
                // Select remains searchable; custom field names can be committed below.
              }
            }}
            dropdownRender={(menu) => (
              <>
                {menu}
                <div style={{ padding: '8px 10px', borderTop: '1px solid var(--color-border-muted)' }}>
                  <Input
                    size="small"
                    placeholder="Custom child field"
                    value={rc.lastItemField || ''}
                    onChange={(e) => update({
                      renderConfig: {
                        ...rc,
                        lastItemField: e.target.value.trim(),
                      },
                    })}
                  />
                </div>
              </>
            )}
          />
          <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 10 }}>
            For an array such as jobPayDetails, ListView displays this field from the last array element.
          </Text>
        </div>

        {/* ── Avatar config ── */}
        {renderType === 'avatar' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px', gap: 8, marginTop: 8 }}>
            <div>
              <Text style={{ fontSize: 11, color: '#555', display: 'block', marginBottom: 3 }}>Name field</Text>
              <Select size="small" showSearch allowClear style={{ width: '100%' }}
                placeholder="e.g. recruiterName" value={rc.labelField || undefined}
                options={subfields.length > 0 ? subfields.map((sf) => ({ value: sf, label: sf })) : []}
                onChange={(v) => update({ renderConfig: { ...rc, labelField: v ?? '' } })} />
            </div>
            <div>
              <Text style={{ fontSize: 11, color: '#555', display: 'block', marginBottom: 3 }}>Color field (opt.)</Text>
              <Select size="small" showSearch allowClear style={{ width: '100%' }}
                placeholder="color" value={rc.colorField || undefined}
                options={subfields.length > 0 ? subfields.map((sf) => ({ value: sf, label: sf })) : []}
                onChange={(v) => update({ renderConfig: { ...rc, colorField: v ?? '' } })} />
            </div>
            <div>
              <Text style={{ fontSize: 11, color: '#555', display: 'block', marginBottom: 3 }}>Max visible</Text>
              <Input size="small" type="number" min={1} max={10} value={rc.maxVisible || 3}
                onChange={(e) => update({ renderConfig: { ...rc, maxVisible: Number(e.target.value) || 3 } })} />
            </div>
          </div>
        )}
      </div>

      <div className="uss-detail-section">
        <div className="uss-detail-label">Display Template</div>
        <TemplateEditor value={field.displayTemplate ?? ''}
          onChange={(v) => update({ displayTemplate: v })}
          placeholder="e.g. {{firstName}} {{lastName}}" />
      </div>

      <div className="uss-detail-section">
        <div className="uss-detail-label">Default Value</div>
        <Input
          size="small"
          allowClear
          value={field.defaultValue ?? ''}
          onChange={(e) => update({ defaultValue: e.target.value })}
          placeholder="Shown when data is empty, e.g. N/A"
        />
        <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 10 }}>
          Used only when this column resolves to an empty, null, missing, or unavailable value.
        </Text>
      </div>

      <div className="uss-detail-section">
        <div className="uss-detail-label">Empty / Group Value Rules</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <Text style={{ fontSize: 11 }}>Treat numeric 0 as empty</Text>
              <Text type="secondary" style={{ display: 'block', fontSize: 10 }}>
                Useful for integer fields where 0 means the value was not provided.
              </Text>
            </div>
            <Switch
              size="small"
              checked={Boolean(rc.treatZeroAsEmpty)}
              onChange={(v) => update({ renderConfig: { ...rc, treatZeroAsEmpty: v } })}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <Text style={{ fontSize: 11 }}>Clean grouped template when a value is empty</Text>
              <Text type="secondary" style={{ display: 'block', fontSize: 10 }}>
                Example: <code>{'{{field1}} - {{field2}}'}</code> shows only field2 when field1 is empty, without the extra dash.
              </Text>
            </div>
            <Switch
              size="small"
              checked={Boolean(rc.cleanEmptyTemplateSeparators)}
              onChange={(v) => update({ renderConfig: { ...rc, cleanEmptyTemplateSeparators: v } })}
            />
          </div>
        </div>
      </div>

      <div className="uss-detail-section">
        <div className="uss-detail-label">
          Link
          <Switch size="small" style={{ marginLeft: 8 }} checked={Boolean(field.isLink)}
            onChange={(v) => update({ isLink: v })} />
        </div>
        {field.isLink && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 4 }}>
            <TemplateEditor value={field.linkTemplate ?? ''} onChange={(v) => update({ linkTemplate: v })}
              placeholder="e.g. /jobs/{{jobId}}" />
            <div className="uss-detail-row">
              <Select size="small" value={field.linkType ?? 'internal'} onChange={(v) => update({ linkType: v })} style={{ width: 120 }}>
                <Option value="internal">internal</Option>
                <Option value="external">external</Option>
              </Select>
              <Select size="small" value={field.linkTarget ?? '_self'} onChange={(v) => update({ linkTarget: v })} style={{ width: 100 }}>
                <Option value="_self">_self</Option>
                <Option value="_blank">_blank</Option>
              </Select>
            </div>
          </div>
        )}
      </div>

      <div className="uss-detail-section">
        <div className="uss-detail-label">Secondary Fields</div>
        <div className="uss-detail-row" style={{ flexWrap: 'wrap' }}>
          {(field.secondaryFields ?? []).map((f) => (
            <span key={f} className="uss-secondary-tag">
              {f}
              <button onClick={() => removeSecondaryField(f)}>×</button>
            </span>
          ))}
        </div>
        <div className="uss-detail-row">
          <Input size="small" placeholder="Add field key" value={newSecondary}
            onChange={(e) => setNewSecondary(e.target.value)}
            onPressEnter={() => { addSecondaryField(newSecondary); setNewSecondary(''); }}
            style={{ width: 160 }} />
          <Button size="small" type="link" icon={<PlusOutlined />}
            onClick={() => { addSecondaryField(newSecondary); setNewSecondary(''); }}>Add</Button>
        </div>
        {(field.secondaryFields?.length > 0) && (
          <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Separator:</Text>
            <Input size="small" value={field.secondarySeparator ?? ''} placeholder="·" style={{ width: 64 }}
              onChange={(e) => update({ secondarySeparator: e.target.value })} />
          </div>
        )}
      </div>

      <div className="uss-detail-section">
        <div className="uss-detail-label">
          Lookup (FK resolve)
          <Switch size="small" style={{ marginLeft: 8 }} checked={Boolean(field.lookup)}
            onChange={(v) => update({ lookup: v ? { collection: '', displayField: '', valueField: '', projectFields: [] } : null })} />
        </div>
        {field.lookup && (
          <div className="uss-lookup-grid">
            <div className="uss-lookup-row">
              <span className="uss-lookup-label">Collection</span>
              <Select size="small" value={field.lookup?.collection ?? ''} onChange={onCollectionChange}
                showSearch placeholder="Select collection" style={{ flex: 1 }}>
                {collections.map((c) => <Option key={c} value={c}>{c}</Option>)}
              </Select>
            </div>
            <div className="uss-lookup-row">
              <span className="uss-lookup-label">Tenant scoped</span>
              <Switch size="small" checked={Boolean(field.lookup?.tenantScoped)}
                onChange={(v) => onLookupFieldChange(field.fieldKey, { tenantScoped: v })} />
            </div>
            <div className="uss-lookup-row">
              <span className="uss-lookup-label">Source Field</span>
              <Input
                size="small"
                value={field.lookup?.localField ?? ''}
                placeholder={`Defaults to ${field.fieldKey}`}
                onChange={(e) => onLookupFieldChange(field.fieldKey, { localField: e.target.value })}
                style={{ flex: 1 }}
              />
            </div>
            {collFields.length > 0 && (
              <>
                <div className="uss-lookup-row">
                  <span className="uss-lookup-label">Value Field</span>
                  <Select size="small" value={field.lookup?.valueField ?? ''}
                    onChange={(v) => onLookupFieldChange(field.fieldKey, { valueField: v })}
                    placeholder="ID field" style={{ flex: 1 }}>
                    {collFields.map((f) => <Option key={f} value={f}>{f}</Option>)}
                  </Select>
                </div>
                <div className="uss-lookup-row">
                  <span className="uss-lookup-label">Display Field</span>
                  <Select size="small" value={field.lookup?.displayField ?? ''}
                    onChange={(v) => onLookupFieldChange(field.fieldKey, { displayField: v })}
                    placeholder="Label field" style={{ flex: 1 }}>
                    {[...collFields, ...subfields].map((f) => <Option key={f} value={f}>{f}</Option>)}
                  </Select>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div className="uss-detail-section">
        <div className="uss-detail-label">
          Derived Array Value
          <Switch size="small" style={{ marginLeft: 8 }} checked={Boolean(field.derived)}
            onChange={(v) => update({ derived: v ? { type: 'arrayField', source: '', matchField: '', matchValue: '', valueField: '', default: null } : null })} />
        </div>
        {field.derived && (
          <div className="uss-lookup-grid">
            {[
              ['Source array', 'source', 'e.g. stages'],
              ['Match field', 'matchField', 'e.g. stageCode'],
              ['Match value', 'matchValue', 'e.g. PO_MSA'],
              ['Value field', 'valueField', 'e.g. status'],
            ].map(([label, key, placeholder]) => (
              <div className="uss-lookup-row" key={key}>
                <span className="uss-lookup-label">{label}</span>
                <Input size="small" value={field.derived?.[key] ?? ''} placeholder={placeholder}
                  onChange={(e) => update({ derived: { ...field.derived, [key]: e.target.value } })} />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="uss-detail-section">
        <div className="uss-detail-label">
          Computed Value
          <Switch size="small" style={{ marginLeft: 8 }} checked={Boolean(field.computed)}
            onChange={(v) => update({ computed: v ? { operator: 'subtract', operands: [], default: 0 } : null })} />
        </div>
        {field.computed && (
          <div className="uss-lookup-grid">
            <div className="uss-lookup-row">
              <span className="uss-lookup-label">Operator</span>
              <Select size="small" value={field.computed?.operator ?? 'subtract'} style={{ flex: 1 }}
                options={['add', 'subtract', 'multiply', 'divide', 'percentage', 'ifNull'].map((value) => ({ value, label: value }))}
                onChange={(operator) => update({ computed: { ...field.computed, operator } })} />
            </div>
            <Text style={{ fontSize: 11 }}>Operands JSON (field or value entries)</Text>
            <Input.TextArea key={JSON.stringify(field.computed?.operands ?? [])} rows={3}
              defaultValue={JSON.stringify(field.computed?.operands ?? [], null, 2)}
              placeholder={'[{"field":"billRate"},{"field":"payRate"}]'}
              onBlur={(e) => {
                try {
                  const operands = JSON.parse(e.target.value || '[]');
                  if (!Array.isArray(operands)) throw new Error('Operands must be an array');
                  update({ computed: { ...field.computed, operands } });
                } catch (error) {
                  message.error(error.message || 'Invalid operands JSON');
                }
              }} />
          </div>
        )}
      </div>

      <div className="uss-detail-section">
        <div className="uss-detail-label" style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Action Buttons <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--color-text-muted)' }}>(max 3)</span></span>
          {(field.actionButtons ?? []).length < 3 && (
            <Button type="link" size="small" icon={<PlusOutlined />} onClick={addActionButton}>Add Icon</Button>
          )}
        </div>
        {(field.actionButtons ?? []).map((btn, i) => (
          <div key={i}>
            <div className="uss-action-btn-row">
              <Upload showUploadList={false} beforeUpload={(f) => { handleIconUpload(i, f); return false; }} accept="image/*">
                <Tooltip title="Click to upload icon">
                  <div className="uss-action-btn-icon-slot">
                    {iconUploading[i]
                      ? <Spin size="small" />
                      : btn.iconUrl
                        ? <img src={btn.iconUrl} alt={`action-${i}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <UploadOutlined style={{ color: '#aaa', fontSize: 14 }} />
                    }
                  </div>
                </Tooltip>
              </Upload>
              <Input size="small" value={btn.name ?? ''} onChange={(e) => updateActionButton(i, { name: e.target.value })}
                placeholder="Tooltip (e.g. Edit)" style={{ width: 110 }} />
              <Radio.Group size="small" value={btn.type ?? 'page'} onChange={(e) => updateActionButton(i, { type: e.target.value })}>
                <Radio value="page">Page</Radio>
                <Radio value="popup">Popup</Radio>
              </Radio.Group>
              <Input size="small" value={btn.routeUrl ?? ''} onChange={(e) => updateActionButton(i, { routeUrl: e.target.value })}
                placeholder="Route URL (e.g. /jobs/:_id)" prefix={<LinkOutlined style={{ color: '#bbb' }} />} style={{ flex: 1 }} />
              <Button size="small" danger type="text" onClick={() => removeActionButton(i)}>✕</Button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 36 }}>
              <Text style={{ fontSize: 11, color: '#999', whiteSpace: 'nowrap' }}>Permission:</Text>
              <Select size="small" allowClear value={btn.permissionKey || undefined}
                onChange={(v) => updateActionButton(i, { permissionKey: v ?? '' })}
                placeholder="Always visible" style={{ width: 180 }}
                options={[{ value: 'edit' }, { value: 'delete' }, { value: 'add' }, { value: 'view' }]}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 36, marginTop: 4, flexWrap: 'wrap' }}>
              <Text style={{ fontSize: 11, color: '#999', whiteSpace: 'nowrap' }}>Show only if:</Text>
              <Input size="small" value={btn.showIfField ?? ''}
                onChange={(e) => updateActionButton(i, { showIfField: e.target.value })}
                placeholder="field (e.g. createdBy)" style={{ width: 180 }} />
              {/* Same operator set + {currentUserId} token as the module Action
                  Rules, so a column button uses the common rule logic — e.g.
                  createdBy eq {currentUserId} shows Resubmit only to the creator. */}
              <Select size="small" value={btn.showIfOperator || 'eq'} style={{ width: 130 }}
                onChange={(v) => updateActionButton(i, { showIfOperator: v })}
                options={ACTION_BUTTON_OPERATORS} />
              {!['isEmpty', 'notEmpty'].includes(btn.showIfOperator || 'eq') && (
                <Select size="small" showSearch allowClear mode="tags" maxCount={1}
                  value={btn.showIfValue ? [btn.showIfValue] : []} style={{ width: 200 }}
                  onChange={(v) => updateActionButton(i, { showIfValue: v[v.length - 1] ?? '' })}
                  placeholder="value or {currentUserId}"
                  options={[{ value: '{currentUserId}', label: '{currentUserId} — signed-in user' }]}
                  filterOption={(input, opt) => (opt?.label ?? opt?.value ?? '').toLowerCase().includes(input.toLowerCase())} />
              )}
              {btn.showIfField && (
                <Button size="small" type="text" danger icon={<CloseOutlined />}
                  onClick={() => updateActionButton(i, { showIfField: '', showIfOperator: '', showIfValue: '' })} />
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// Operators for a column action button's "Show only if" — the same set the
// module Action Rules use (utils/actionRuleEngine), so column, row, toolbar and
// detail actions all gate on one common logic.
const ACTION_BUTTON_OPERATORS = [
  { value: 'eq', label: 'equals' },
  { value: 'ne', label: 'not equals' },
  { value: 'isEmpty', label: 'is empty' },
  { value: 'notEmpty', label: 'is not empty' },
  { value: 'contains', label: 'contains' },
  { value: 'includes', label: 'list includes (exact)' },
  { value: 'notIncludes', label: 'list excludes (exact)' },
];

const FILTER_DROPDOWN_SOURCES = [
  { label: 'Module data (distinct)', value: 'module' },
  { label: 'Masters collection', value: 'master' },
  { label: 'Group value', value: 'group' },
];

const DROPDOWN_FIELD_TYPES = ['select', 'radio', 'checkbox'];

const FILTER_INPUT_TYPE_OPTIONS = [
  { label: 'Dropdown', value: 'dropdown' },
  { label: 'Checkbox', value: 'checkbox' },
  { label: 'Radio', value: 'radio' },
  { label: 'Date Range', value: 'datepicker' },
  { label: 'Number Range', value: 'number-range' },
];

function FormFilterDetails({ field, update, configType, collections, collFields, onCollectionChange, onLookupFieldChange, availableFields = [] }) {
  const isDropdownType = DROPDOWN_FIELD_TYPES.includes(field.type);
  const hasLookup = Boolean(field.lookup);

  function handleLookupToggle(v) {
    update({ lookup: v ? { collection: '', displayField: '', valueField: '', projectFields: [] } : null });
  }

  return (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Text style={{ fontSize: 12 }}>Editable</Text>
        <Switch size="small" checked={Boolean(field.isEditable)} onChange={(v) => update({ isEditable: v })} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Text style={{ fontSize: 12 }}>Mandatory</Text>
        <Switch size="small" checked={Boolean(field.ismandatory)} onChange={(v) => update({ ismandatory: v })} />
      </div>
      {configType === 'filter' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text style={{ fontSize: 12 }}>Value Input</Text>
          <Select
            size="small"
            value={field.filterInputType ?? 'dropdown'}
            onChange={(v) => update({ filterInputType: v })}
            options={FILTER_INPUT_TYPE_OPTIONS}
            style={{ width: 140 }}
          />
        </div>
      )}
      {configType === 'filter' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text style={{ fontSize: 12 }}>Default</Text>
          <Switch size="small" checked={Boolean(field.isDefaultFilter)} onChange={(v) => update({ isDefaultFilter: v })} />
        </div>
      )}
      {configType === 'filter' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text style={{ fontSize: 12 }}>Lookup</Text>
          <Switch size="small" checked={hasLookup} onChange={handleLookupToggle} />
        </div>
      )}
      {configType === 'filter' && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 360 }}>
            <Text style={{ fontSize: 12 }}>Group Value Fields</Text>
            <Select
              size="small"
              mode="multiple"
              allowClear
              value={Array.isArray(field.filterGroupFields) ? field.filterGroupFields : []}
              onChange={(values) => update({ filterGroupFields: values })}
              options={(availableFields ?? []).map((f) => ({
                label: f.fieldName ?? f.label ?? f.fieldKey,
                value: f.fieldKey,
              })).filter((f) => f.value)}
              placeholder="e.g. First Name + Last Name"
              style={{ minWidth: 250 }}
            />
          </div>
          {(field.filterGroupFields ?? []).length > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text style={{ fontSize: 12 }}>Group Separator</Text>
              <Input
                size="small"
                value={field.filterGroupSeparator ?? ' '}
                onChange={(e) => update({ filterGroupSeparator: e.target.value })}
                placeholder="space"
                style={{ width: 90 }}
              />
            </div>
          )}
        </>
      )}
      {configType === 'filter' && hasLookup && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <Select
            size="small"
            value={field.lookup?.collection || undefined}
            onChange={onCollectionChange}
            showSearch
            placeholder="Collection"
            style={{ width: 160 }}
            options={(collections ?? []).map((c) => ({ label: c, value: c }))}
          />
          {(collFields ?? []).length > 0 && (
            <>
              <Select
                size="small"
                value={field.lookup?.valueField || undefined}
                onChange={(v) => onLookupFieldChange(field.fieldKey, { valueField: v })}
                showSearch
                placeholder="Value field (ID)"
                style={{ width: 160 }}
                options={(collFields ?? []).map((f) => ({ label: f, value: f }))}
              />
              <Input
                size="small"
                value={field.lookup?.displayField ?? ''}
                onChange={(e) => onLookupFieldChange(field.fieldKey, { displayField: e.target.value })}
                placeholder="Display field (e.g. name)"
                style={{ width: 180 }}
              />
            </>
          )}
        </div>
      )}
      {isDropdownType && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text style={{ fontSize: 12 }}>Source</Text>
            <Select
              size="small"
              value={field.dataSource ?? 'module'}
              onChange={(v) => update({ dataSource: v, groupName: '' })}
              options={FILTER_DROPDOWN_SOURCES}
              style={{ width: 170 }}
            />
          </div>
          {(field.dataSource ?? 'module') === 'group' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text style={{ fontSize: 12 }}>Group Name</Text>
              <Input
                size="small"
                value={field.groupName ?? ''}
                onChange={(e) => update({ groupName: e.target.value })}
                placeholder="e.g. skills"
                style={{ width: 120 }}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Field row inside config modal
// ─────────────────────────────────────────────────────────────
function FieldRow({ field, index, configType, moduleKey, onUpdate, onReorder, collections, onFetchCollFields, dragFromRef, availableFields }) {
  const [expanded, setExpanded] = useState(false);
  const [labelEdit, setLabelEdit] = useState(false);
  const [labelVal, setLabelVal] = useState(field.fieldName);
  const [collFields, setCollFields] = useState([]);
  const [subfields, setSubfields] = useState([]);
  const [over, setOver] = useState(false);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    queueMicrotask(() => {
      setLabelVal(field.fieldName);
    });
  }, [field.fieldName]);

  useEffect(() => {
    if (!expanded || configType !== 'listView' || !field.fieldKey) return;
    let cancelled = false;
    const apiModule = field.apiModule ?? API_MODULE_MAP[moduleKey] ?? moduleKey;

    getArraySubfields(apiModule, field.fieldKey)
      .then((items) => {
        if (!cancelled) setSubfields(Array.isArray(items) ? items : []);
      })
      .catch(() => {
        if (!cancelled) setSubfields([]);
      });

    return () => { cancelled = true; };
  }, [expanded, configType, field.apiModule, field.fieldKey, moduleKey]);

  function handleDragStart(e) { dragFromRef.current = index; e.dataTransfer.effectAllowed = 'move'; setDragging(true); }
  function handleDragEnd() { setDragging(false); setOver(false); }
  function handleDragOver(e) { e.preventDefault(); setOver(true); }
  function handleDragLeave() { setOver(false); }
  function handleDrop(e) {
    e.preventDefault(); setOver(false);
    if (dragFromRef.current !== null && dragFromRef.current !== index) onReorder(dragFromRef.current, index);
    dragFromRef.current = null;
  }

  function update(patch) { onUpdate(index, patch); }

  function commitLabel() {
    setLabelEdit(false);
    const trimmed = labelVal.trim();
    if (trimmed && trimmed !== field.fieldName) update({ fieldName: trimmed });
    else setLabelVal(field.fieldName);
  }

  async function onCollectionChange(col) {
    update({ lookup: { ...field.lookup, collection: col, displayField: '', valueField: '', projectFields: [] } });
    const f = await onFetchCollFields(col);
    setCollFields(f);
  }

  async function onLookupFieldChange(fKey, patch) {
    update({ lookup: { ...field.lookup, ...patch } });
    if (patch.valueField) {
      try { setSubfields(await getArraySubfields(moduleKey, fKey)); } catch { setSubfields([]); }
    }
  }

  function addSecondaryField(val) {
    const arr = field.secondaryFields ?? [];
    if (!val || arr.includes(val)) return;
    update({ secondaryFields: [...arr, val] });
  }
  function removeSecondaryField(v) { update({ secondaryFields: (field.secondaryFields ?? []).filter((x) => x !== v) }); }

  function addActionButton() {
    const btns = field.actionButtons ?? [];
    if (btns.length >= 3) return;
    update({ actionButtons: [...btns, { name: '', iconUrl: '', iconPath: '', type: 'page', routeUrl: '', permissionKey: '' }] });
  }
  function updateActionButton(i, patch) {
    const btns = [...(field.actionButtons ?? [])];
    btns[i] = { ...btns[i], ...patch };
    update({ actionButtons: btns });
  }
  function removeActionButton(i) {
    const btns = [...(field.actionButtons ?? [])];
    btns.splice(i, 1);
    update({ actionButtons: btns });
  }

  return (
    <>
      <div
        draggable
        onDragStart={handleDragStart} onDragEnd={handleDragEnd}
        onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
        className={`uss-field-row${over ? ' drag-over' : ''}${dragging ? ' dragging' : ''}`}
        style={{ gridTemplateColumns: '24px 1fr 80px 56px' }}
      >
        <span className="uss-drag-handle"><HolderOutlined /></span>

        <span className="uss-field-name">
          {field.isVirtual && <span className="uss-virtual-tag">virtual</span>}
          {labelEdit ? (
            <input autoFocus value={labelVal}
              onChange={(e) => setLabelVal(e.target.value)} onBlur={commitLabel}
              onKeyDown={(e) => { if (e.key === 'Enter') commitLabel(); if (e.key === 'Escape') { setLabelEdit(false); setLabelVal(field.fieldName); } }}
              onClick={(e) => e.stopPropagation()} />
          ) : (
            <Tooltip title="Double-click to rename">
              <span style={{ cursor: 'text', borderBottom: '1px dashed var(--color-border-muted)' }}
                onDoubleClick={(e) => { e.stopPropagation(); setLabelEdit(true); }}>
                {field.fieldName}
              </span>
            </Tooltip>
          )}
          <span className="uss-field-key">{field.fieldKey}</span>
        </span>

        <span className="uss-toggle-cell">
          <Switch size="small" checked={field.isVisible} onChange={(v) => update({ isVisible: v })} />
        </span>

        <span className="uss-toggle-cell">
          <Button type="text" size="small"
            icon={expanded ? <DownOutlined /> : <RightOutlined />}
            onClick={() => setExpanded((p) => !p)}
            style={{ color: 'var(--color-text-muted)' }} />
        </span>
      </div>

      {expanded && (
        <div className="uss-detail-panel">
          {configType === 'listView' ? (
            <ListViewDetails
              field={field} collections={collections}
              collFields={collFields} subfields={subfields}
              onCollectionChange={onCollectionChange}
              onLookupFieldChange={onLookupFieldChange}
              addSecondaryField={addSecondaryField}
              removeSecondaryField={removeSecondaryField}
              addActionButton={addActionButton}
              updateActionButton={updateActionButton}
              removeActionButton={removeActionButton}
              moduleKey={moduleKey} fieldKey={field.fieldKey}
              update={update}
            />
          ) : (
            <FormFilterDetails
              field={field}
              update={update}
              configType={configType}
              collections={collections}
              collFields={collFields}
              onCollectionChange={onCollectionChange}
              onLookupFieldChange={onLookupFieldChange}
              availableFields={availableFields}
            />
          )}
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// Module field config (modal body) — role-scoped
// ─────────────────────────────────────────────────────────────
export default function ModuleFieldConfigEditor({ role, configType }) {
  const [moduleFields, setModuleFields] = useState({});
  const [dataVisibilityModes, setDataVisibilityModes] = useState({});
  const [dataOwnerFields, setDataOwnerFields] = useState({});
  const [moduleTabs, setModuleTabs] = useState(DEFAULT_MODULE_TABS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState({});
  const [activeModule, setActiveModule] = useState('job');
  const [collections, setCollections] = useState([]);
  const collFieldsCache = useRef({});
  const dragFromRef = useRef(null);

  const loadFieldConfig = useCallback(async (currentRole = role, currentConfigType = configType) => {
    const [rawModules, colls] = await Promise.all([
      getAvailableModules().catch(() => []),
      getAvailableCollections(),
    ]);

    const tabs = normalizeModuleTabs(rawModules);
    if (!currentConfigType || currentConfigType === 'listView') {
      const configByModule = await getRoleModuleFieldsWithMeta(currentRole, currentConfigType, tabs);
      const fields = Object.fromEntries(Object.entries(configByModule).map(([key, value]) => [key, value.fields]));
      const visibilityModes = Object.fromEntries(Object.entries(configByModule).map(([key, value]) => [key, value.dataVisibilityMode ?? 'all']));
      const ownerFields = Object.fromEntries(Object.entries(configByModule).map(([key, value]) => [key, value.dataOwnerField ?? 'createdBy']));
      return { tabs, colls, fields, visibilityModes, ownerFields };
    }
    const fields = await getRoleModuleFields(currentRole, currentConfigType, tabs);
    return { tabs, colls, fields, visibilityModes: {}, ownerFields: {} };
  }, [configType, role]);

  useEffect(() => {
    if (!role) return;
    let cancelled = false;
    queueMicrotask(() => {
      setLoading(true);
      (async () => {
        try {
          const { tabs, colls, fields, visibilityModes, ownerFields } = await loadFieldConfig();
          if (cancelled) return;

          setModuleTabs(tabs);
          setCollections(colls);
          setActiveModule((current) => (
            tabs.some((tab) => tab.key === current) ? current : tabs[0]?.key ?? ''
          ));
          setModuleFields(fields);
          setDataVisibilityModes(visibilityModes ?? {});
          setDataOwnerFields(ownerFields ?? {});
        } catch {
          if (!cancelled) message.error('Failed to load field config');
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
    });
    return () => { cancelled = true; };
  }, [loadFieldConfig, role]);

  const fields = moduleFields[activeModule] ?? [];

  function setFields(module, updater) {
    setModuleFields((prev) => ({
      ...prev,
      [module]: typeof updater === 'function' ? updater(prev[module] ?? []) : updater,
    }));
  }

  function handleUpdate(idx, patch) {
    setFields(activeModule, (prev) => prev.map((f, i) => i === idx ? { ...f, ...patch } : f));
  }

  function handleReorder(from, to) {
    setFields(activeModule, (prev) => {
      const arr = [...prev];
      const [moved] = arr.splice(from, 1);
      arr.splice(to, 0, moved);
      return arr.map((f, i) => ({ ...f, order: i }));
    });
  }

  async function fetchCollFields(collection) {
    if (collFieldsCache.current[collection]) return collFieldsCache.current[collection];
    try {
      const f = await getCollectionFields(collection);
      collFieldsCache.current[collection] = f;
      return f;
    } catch { return []; }
  }

  async function handleSave() {
    const mod = activeModule;
    setSaving((p) => ({ ...p, [mod]: true }));
    try {
      await updateRoleModuleFieldConfig(
        role, mod, fields, configType, moduleTabs,
        dataVisibilityModes[mod] ?? 'all',
        dataOwnerFields[mod] ?? 'createdBy',
      );
      message.success('Saved');
    } catch {
      message.error('Save failed');
    } finally {
      setSaving((p) => ({ ...p, [mod]: false }));
    }
  }

  return (
    <Spin spinning={loading}>
      <Tabs size="small" activeKey={activeModule} onChange={setActiveModule}
        items={moduleTabs.map((t) => ({ key: t.key, label: t.label }))} />
      {(!configType || configType === 'listView') && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '4px 0 14px' }}>
          <Text strong style={{ minWidth: 120 }}>Data Visibility</Text>
          <Select
            size="small"
            style={{ width: 290 }}
            value={dataVisibilityModes[activeModule] ?? 'all'}
            onChange={(value) => setDataVisibilityModes((prev) => ({ ...prev, [activeModule]: value }))}
            options={[
              { value: 'all', label: 'All Data' },
              { value: 'reportingManager', label: 'Reporting Manager Wise' },
              { value: 'self', label: 'My Created Data' },
            ]}
          />
          <Text type="secondary">
            Reporting Manager Wise shows data owned by the user and users reporting under them.
          </Text>
        </div>
      )}
      {(!configType || configType === 'listView') && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '-4px 0 14px' }}>
          <Text strong style={{ minWidth: 120 }}>Created By Field</Text>
          <Input
            size="small"
            style={{ width: 290 }}
            value={dataOwnerFields[activeModule] ?? 'createdBy'}
            placeholder="createdBy"
            onChange={(event) => setDataOwnerFields((prev) => ({
              ...prev,
              [activeModule]: event.target.value,
            }))}
            onBlur={() => setDataOwnerFields((prev) => ({
              ...prev,
              [activeModule]: String(prev[activeModule] || '').trim() || 'createdBy',
            }))}
          />
          <Text type="secondary">
            Defaults to createdBy. Change this only when the module stores its creator/owner user ID in another field.
          </Text>
        </div>
      )}
      <div className="uss-field-header" style={{ gridTemplateColumns: '24px 1fr 80px 56px' }}>
        <span /><span>Field</span>
        <span style={{ textAlign: 'center' }}>Visible</span>
        <span />
      </div>
      <div className="uss-field-list">
        {fields.length === 0 && !loading && <div className="uss-empty">No fields found.</div>}
        {fields.map((field, idx) => (
          <FieldRow key={`${field.fieldKey ?? idx}-${activeModule}`}
            field={field} index={idx}
            configType={configType} moduleKey={activeModule}
            onUpdate={handleUpdate} onReorder={handleReorder}
            collections={collections} onFetchCollFields={fetchCollFields}
            dragFromRef={dragFromRef}
            availableFields={fields}
          />
        ))}
      </div>
      <div className="uss-modal-footer">
        <Button type="primary" size="small" loading={saving[activeModule]} onClick={handleSave}>
          Save {moduleTabs.find((t) => t.key === activeModule)?.label}
        </Button>
      </div>
    </Spin>
  );
}
